import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Trophy, Settings, Headphones } from 'lucide-react';
import { IMAGES } from '../mock';

// Color palettes
const PALETTES = [
  {
    id: 1,
    name: 'TRACK DAY',
    subtitle: 'Energetic, high-contrast. Purple becomes a "premium tier" badge.',
    colors: {
      primary: '#FF6B00',
      primaryDark: '#E25500',
      accent: '#FF6B00',
      premium: '#4B1D8E',
    },
  },
  {
    id: 2,
    name: 'SUNSET GARAGE',
    subtitle: 'Warm gradient feel. Big surfaces use a 3-color blend.',
    colors: {
      primary: '#FF6B00',
      primaryDark: '#E25500',
      accent: '#FF6B00',
      premium: '#7C2DBF',
      gradient: 'linear-gradient(90deg, #4B1D8E 0%, #FF6B00 50%, #FF6B00 100%)',
    },
  },
  {
    id: 3,
    name: 'VELVET CARBON',
    subtitle: 'Luxurious, restrained. Purple appears in dividers, eyebrows, and the logo crown.',
    colors: {
      primary: '#FF6B00',
      primaryDark: '#E25500',
      accent: '#FF6B00',
      premium: '#5A2D8C',
    },
  },
  {
    id: 4,
    name: 'TRIPLE-STRIPE HERITAGE',
    subtitle: 'Motorsport livery. A 3-color stripe runs along major edges; purple = Black Label.',
    colors: {
      primary: '#FF6B00',
      primaryDark: '#E25500',
      accent: '#FF6B00',
      premium: '#4B1D8E',
    },
  },
];

const Stripe = ({ vertical = false }) => (
  <div className={vertical ? 'flex flex-col w-1' : 'flex h-1 w-full'}>
    <div className="flex-1 bg-[#FF6B00]" />
    <div className="flex-1 bg-[#FF6B00]" />
    <div className="flex-1 bg-[#4B1D8E]" />
  </div>
);

const PaletteSection = ({ palette }) => {
  const { id, name, subtitle, colors } = palette;
  const isOpt2 = id === 2;
  const isOpt3 = id === 3;
  const isOpt4 = id === 4;

  return (
    <section className="border-t border-white/10">
      {/* Heading bar */}
      <div className="bg-neutral-950 py-8 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto flex items-baseline gap-6 flex-wrap">
          <span className="font-display text-xs tracking-[0.3em] text-neutral-500">OPTION {id}</span>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight text-white">{name}</h2>
          <p className="text-neutral-400 text-sm max-w-2xl">{subtitle}</p>
        </div>
        {/* Color swatches */}
        <div className="max-w-[1400px] mx-auto mt-6 flex flex-wrap gap-3">
          {[
            { label: 'Primary (buttons)', color: colors.primary },
            { label: 'Accent (lines, eyebrow)', color: colors.accent },
            { label: 'Premium / Black Label', color: colors.premium },
          ].map((c) => (
            <div key={c.label} className="flex items-center gap-2 bg-black/40 px-3 py-2 border border-white/10">
              <span className="block h-5 w-5" style={{ backgroundColor: c.color }} />
              <div className="text-xs">
                <div className="text-white">{c.label}</div>
                <div className="text-neutral-500 font-mono">{c.color}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mini hero */}
      <div className="relative overflow-hidden border-t border-white/5">
        {isOpt4 && <Stripe />}
        <div
          className="relative px-6 lg:px-10 py-14"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 100%), url(${IMAGES.heroCar})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-8" style={{ backgroundColor: isOpt3 ? colors.premium : colors.accent }} />
              <span className="font-display tracking-[0.28em] text-[11px] font-semibold" style={{ color: isOpt3 ? colors.premium : colors.accent }}>
                PRECISION. PERFORMANCE. STYLE.
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight max-w-2xl">
              CANADA&rsquo;S LARGEST SELECTION OF<br />
              PREMIUM CARBON FIBER COMPONENTS
            </h3>
            <div className="mt-6 flex gap-4 flex-wrap">
              <button
                className="px-7 py-4 font-display tracking-[0.22em] text-sm font-bold text-white"
                style={{ backgroundColor: colors.primary }}
              >
                SHOP McLAREN
              </button>
              <button className="px-7 py-4 font-display tracking-[0.22em] text-sm font-bold text-white border-2 border-white/70">
                OUR SERVICES
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feature row + premium card */}
      <div className="bg-black px-6 lg:px-10 py-12">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-3 gap-8 items-stretch">
          {/* Standard feature */}
          <div className="bg-neutral-950 border border-white/10 p-7">
            <div className="font-display tracking-[0.3em] text-[10px] font-semibold mb-2" style={{ color: colors.accent }}>
              FEATURED PRODUCT
            </div>
            <h4 className="font-display text-2xl text-white mb-1">DRY CARBON HOOD</h4>
            <div className="text-neutral-400 text-sm">BMW M2 G87 · from $2,495</div>
            <button className="mt-5 inline-flex items-center gap-2 px-5 py-3 font-display tracking-[0.22em] text-xs font-bold text-white" style={{ backgroundColor: colors.primary }}>
              VIEW <ArrowRight size={14} />
            </button>
          </div>

          {/* Premium / Black Label card */}
          <div className="relative bg-neutral-950 border p-7" style={{ borderColor: isOpt4 ? colors.premium : 'rgba(255,255,255,0.1)' }}>
            {isOpt4 && (
              <div className="absolute top-0 right-0 px-3 py-1 font-display tracking-[0.2em] text-[10px] font-bold text-white" style={{ backgroundColor: colors.premium }}>
                BLACK LABEL
              </div>
            )}
            <div className="font-display tracking-[0.3em] text-[10px] font-semibold mb-2" style={{ color: isOpt3 || isOpt4 ? colors.premium : colors.accent }}>
              FORTUNE BLACK LABEL
            </div>
            <h4 className="font-display text-2xl text-white mb-1">FORGED ALPHA WHEEL</h4>
            <div className="text-neutral-400 text-sm">6061-T6 Monoblock · from $595</div>
            <button
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 font-display tracking-[0.22em] text-xs font-bold text-white"
              style={{ backgroundColor: id === 1 ? colors.premium : colors.primary }}
            >
              CONFIGURE <ArrowRight size={14} />
            </button>
          </div>

          {/* Why Choose Us tile */}
          <div className="bg-neutral-950 border border-white/10 p-7">
            <div className="grid grid-cols-2 gap-5">
              {[
                { Icon: ShieldCheck, label: 'PREMIUM' },
                { Icon: Trophy, label: 'PROVEN' },
                { Icon: Settings, label: 'CUSTOM' },
                { Icon: Headphones, label: 'SUPPORT' },
              ].map(({ Icon, label }) => (
                <div key={label} className="text-center">
                  <Icon size={28} strokeWidth={1.5} style={{ color: isOpt3 ? colors.premium : colors.accent }} className="mx-auto mb-2" />
                  <div className="text-[11px] tracking-[0.2em] font-display text-white">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA banner */}
      <div className="relative overflow-hidden">
        {isOpt4 && <Stripe />}
        <div
          className="px-6 lg:px-10 py-14 text-white"
          style={
            isOpt2
              ? { background: colors.gradient }
              : { backgroundColor: colors.primary }
          }
        >
          <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="font-display tracking-[0.3em] text-[11px] font-semibold mb-2 text-white/85">START YOUR BUILD</div>
              <h3 className="font-display text-3xl md:text-5xl tracking-tight">
                READY TO BUILD<br />SOMETHING UNFORGETTABLE?
              </h3>
            </div>
            <button
              className="inline-flex items-center gap-3 px-8 py-5 font-display tracking-[0.22em] text-sm font-bold bg-white"
              style={{ color: isOpt2 ? colors.premium : colors.primary }}
            >
              REQUEST A QUOTE <ArrowRight size={18} />
            </button>
          </div>
        </div>
        {isOpt4 && <Stripe />}
      </div>
    </section>
  );
};

const Palettes = () => {
  return (
    <div className="bg-black text-white pt-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-10">
        <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">BRAND COLOR PREVIEW</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight">FOUR DIRECTIONS</h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Mockups of how Orange + Red + Royal Purple can work together. Each option uses the same base layout so the only thing changing is the color treatment.
        </p>
        <Link to="/" className="inline-block mt-6 text-sm font-display tracking-[0.22em] text-neutral-300 hover:text-white">
          &larr; BACK TO SITE
        </Link>
      </div>
      {PALETTES.map((p) => (
        <PaletteSection key={p.id} palette={p} />
      ))}
    </div>
  );
};

export default Palettes;
